import cx from 'classnames'
import React, {
  HTMLInputTypeAttribute,
  InputHTMLAttributes,
  LabelHTMLAttributes
} from 'react'
import { useFormContext } from 'react-hook-form'
import { RegisterOptions } from 'react-hook-form/dist/types/validator'

import { FormErrorMessage } from './ErrorMessage'

interface HookFormTextProps {
  className?: string
  name: string
  defaultValue?: string
  inputProps?: InputHTMLAttributes<HTMLInputElement> & {
    'data-sel'?: string
    'data-testid'?: string
  }
  isHorizontal?: boolean
  locationFinderPlaceholder?: string
  labelProps?: LabelHTMLAttributes<HTMLLabelElement>
  disabled?: boolean
  type?: HTMLInputTypeAttribute
  placeholder: string
  children?: React.ReactNode
  registerOptions?: RegisterOptions
}

export const HookFormText = ({
  className,
  labelProps,
  name,
  disabled,
  isHorizontal,
  type = 'text',
  locationFinderPlaceholder = '',
  inputProps,
  placeholder,
  children,
  registerOptions = {}
}: HookFormTextProps) => {
  const { register, formState, getFieldState, watch } = useFormContext()
  const { isValidating } = formState
  const { error, isTouched } = getFieldState(name, formState)

  const value = watch(name)

  const displayError = error?.message as string
  const handleLabelFocus = (event: React.FocusEvent<HTMLLabelElement>) => {
    window._wt({
      category: 'input',
      action: 'focus',
      values: [{ type: 'name', value: name }]
    })
    labelProps?.onFocus?.(event)
  }

  const textInput = (
    <>
      <label
        className={cx(
          className,
          '[ f-d-bl f-pos-rel f-c-fo-pri f-p-h-020 ] FormText-wrapper',
          displayError ? 'f-c-bo-war f-m-b-010' : 'f-m-b-020',
          disabled && 'FormText--disabled',
          isValidating && 'is-validating',
          value && !displayError && isTouched && 'f-c-bo-suc400'
        )}
        {...labelProps}
        onFocus={handleLabelFocus}>
        <span
          className={cx('[ f-pos-ab f-c-fo-mut f-t-body ] FormTextLabel', {
            'has-value': value
          })}>
          {placeholder}
        </span>
        <input
          type={type}
          className='[ f-p-a-0 ] FormText'
          placeholder={locationFinderPlaceholder}
          disabled={disabled}
          {...register(name, registerOptions)}
          {...inputProps}
        />
        {children}
      </label>
      {displayError && <FormErrorMessage>{displayError}</FormErrorMessage>}
    </>
  )

  if (isHorizontal) {
    return <div className='[ f-pos-rel ] full-width:s'>{textInput}</div>
  }

  return textInput
}
